import React, { useEffect, useRef } from 'react';
import { TranscriptEntry, Participant } from '../../store/conversationSlice';
import { getSpeakerLabel, getSpeakerColor } from '../../config/participants';
import EarlierTag from '../EarlierTag';

/** The id LiveTranscriptBar gives the line still being spoken (training). */
export const FORMING_LINE_ID = 'interim';

/** The bubble's clock, "14:30:45" in local time (as on macOS and Windows).
 *  A stamp without a zone is UTC, as the services write it. */
export const formatBubbleTime = (timestamp: string): string => {
  let ts = timestamp;
  if (!timestamp.endsWith('Z') && !timestamp.includes('+') && !timestamp.includes('-', 10)) {
    ts = `${timestamp}Z`;
  }
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

interface TranscriptFeedProps {
  transcripts: TranscriptEntry[];
  participants: Participant[];
  salespersonLabel?: string;
  className?: string;
}

// Adaptive transcript-feed container — fills whatever parent it's placed in
// (h-full/flex-column) rather than the fixed-height dropdown LiveTranscriptBar
// uses. Shared bubble-list rendering for both mount points.
export const TranscriptFeed: React.FC<TranscriptFeedProps> = ({
  transcripts,
  participants,
  salespersonLabel = 'Salesperson',
  className = '',
}) => {
  const feedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (feedRef.current && transcripts.length > 0) {
      requestAnimationFrame(() => {
        if (feedRef.current) {
          feedRef.current.scrollTop = feedRef.current.scrollHeight;
        }
      });
    }
  }, [transcripts]);

  return (
    <div
      ref={feedRef}
      className={`min-h-0 overflow-y-auto p-3 ${className || 'h-full'}`}
      style={{ scrollBehavior: 'smooth' }}
    >
      {transcripts.length === 0 ? (
        <div className="space-y-2 py-6 text-center">
          <p className="text-[13px] font-medium text-ink">No transcripts yet</p>
          <p className="text-[11px] text-ink-muted">Start speaking to see the live transcript</p>
        </div>
      ) : (
        <div className="space-y-2">
          {transcripts.map((transcript) => {
            const isCustomer = transcript.speaker === 'customer';
            const label = getSpeakerLabel(transcript, participants, { salespersonLabel });
            const color = getSpeakerColor(transcript, participants);
            // A line restored from the saved session (said before this client
            // joined or while it reconnected) is muted and marked, not coloured.
            const isRestored = !!transcript.isRestored;
            // The line still being spoken (LiveTranscriptBar's interim row) is drawn at 60%.
            const isForming = transcript.id === FORMING_LINE_ID;
            const time = formatBubbleTime(transcript.timestamp);
            // The macOS bubble: the speaker and time above it, the rep's on the
            // right in their palette colour with white text, a customer's on the left
            // in their pastel with ink text, and 40 kept free on the other side.
            return (
              <div
                key={transcript.id}
                className={`flex flex-col gap-0.5 ${isCustomer ? 'items-start pr-10' : 'items-end pl-10'}`}
              >
                <span className="text-[10px] font-medium text-ink-muted">
                  {label}
                  {isRestored && (
                    <>
                      {' \u00B7 '}
                      <EarlierTag />
                    </>
                  )}
                  {time && ` \u00B7 ${time}`}
                </span>
                <div
                  className={`max-w-full rounded-control px-2.5 py-1.5${
                    isRestored ? ' bg-surface-subtle border border-hairline text-ink-muted' : isCustomer ? ' text-ink' : ''
                  }${isForming ? ' opacity-60' : ''}`}
                  style={
                    isRestored
                      ? undefined
                      : isCustomer
                      ? { backgroundColor: color.bg }
                      : { backgroundColor: color.bg, color: color.text }
                  }
                >
                  <p className="break-words text-xs">{transcript.text}</p>
                </div>
              </div>
            );
          })}
          <div className="h-1" />
        </div>
      )}
    </div>
  );
};
